import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { IconCalendarClockOutline18, IconSummitFlagOutline18 } from 'nucleo-ui-outline-18'
import { useMemo } from 'react'
import { toast } from 'sonner'
import { SettingsPage, SettingsRow, SettingsSection } from '~/components/settings/settings-page'
import { SettingsTextField } from '~/components/settings/settings-text-field'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Spinner } from '~/components/ui/spinner'
import { Switch } from '~/components/ui/switch'
import { titleFor } from '~/lib/route-title'
import { useWorkspace, type Workspace } from '~/lib/workspace-context'
import { api } from '~convex/_generated/api'

const SPRINT_LENGTH_MIN = 1
const SPRINT_LENGTH_MAX = 60
const UPCOMING_COUNT_MAX = 12

// Sunday Jan 7 2024 in UTC — anchors weekday labels to a fixed reference week.
const WEEKDAY_ANCHOR_UTC = Date.UTC(2024, 0, 7)

export const Route = createFileRoute('/_authenticated/$workspaceSlug/settings/sprints')({
  component: SprintSettings,
  head: () => ({ meta: [{ title: titleFor(msg`Sprint settings`) }] }),
})

function SprintSettings() {
  const { t } = useLingui()
  const workspace = useWorkspace()
  const isAdmin = workspace.role === 'admin'
  const sprintsEnabled = workspace.sprintsEnabled === true
  const { mutateAsync: setSprintsEnabled, isPending: isToggling } = useMutation({
    mutationFn: useConvexMutation(api.workspaces.setSprintsEnabled),
  })
  const { data: cadence, isPending } = useQuery({
    ...convexQuery(api.sprintConfig.get, { workspaceId: workspace._id }),
    enabled: sprintsEnabled,
  })

  const handleToggle = (enabled: boolean) => {
    setSprintsEnabled({ enabled, workspaceId: workspace._id }).catch(() => {
      toast.error(t`Couldn't update the sprint setting.`)
    })
  }

  return (
    <SettingsPage>
      <SettingsSection
        description={
          isAdmin ? (
            <Trans>Weekly task cycles with burndown, for studios that work in iterations.</Trans>
          ) : (
            <Trans>Only workspace admins can change this setting.</Trans>
          )
        }
        icon={IconSummitFlagOutline18}
        title={t`Sprints`}
      >
        <SettingsRow
          description={t`Turning this off hides sprints; existing sprints are kept.`}
          label={t`Enable sprints`}
        >
          <Switch
            aria-label={t`Enable sprints`}
            checked={sprintsEnabled}
            disabled={!isAdmin || isToggling}
            onCheckedChange={handleToggle}
          />
        </SettingsRow>
      </SettingsSection>
      {sprintsEnabled ? (
        cadence ? (
          <SprintFields
            cadence={cadence}
            isAdmin={isAdmin}
            key={workspace._id}
            workspaceId={workspace._id}
          />
        ) : isPending ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : null
      ) : null}
    </SettingsPage>
  )
}

type Cadence = {
  startDayOfWeek: number
  cadenceLengthDays: number
  upcomingCount: number
}

/**
 * Each field saves on its own — `sprintConfig.update` takes every argument as
 * optional and merges against the stored cadence, so a single-field patch is
 * exactly what it expects. No local baseline is needed: the query is a live
 * subscription, so the inputs re-sync from the server after every save.
 */
const SprintFields = ({
  cadence,
  isAdmin,
  workspaceId,
}: {
  cadence: Cadence
  isAdmin: boolean
  workspaceId: Workspace['_id']
}) => {
  const { t, i18n } = useLingui()
  const updateCadence = useConvexMutation(api.sprintConfig.update)

  const weekdayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.locale, {
      timeZone: 'UTC',
      weekday: 'long',
    })
    return Array.from({ length: 7 }, (_, dayIndex) => {
      const label = formatter.format(new Date(WEEKDAY_ANCHOR_UTC + dayIndex * 86_400_000))
      return label.charAt(0).toUpperCase() + label.slice(1)
    })
  }, [i18n.locale])

  // Value→label map for the weekday Select. Without it `<SelectValue/>`
  // renders the raw value ("0") instead of "Sunday" — see .claude/CLAUDE.md.
  const weekdayItems = useMemo(() => {
    const map: Record<string, string> = {}
    for (const [dayIndex, label] of weekdayLabels.entries()) {
      map[String(dayIndex)] = label
    }
    return map
  }, [weekdayLabels])

  /** Rejects anything that isn't a whole number inside `[min, max]`. */
  const boundedInteger = (value: string, min: number, max: number): string | null => {
    const parsed = Number(value)
    if (!Number.isInteger(parsed) || parsed < min || parsed > max) {
      return t`Must be a whole number between ${min} and ${max}.`
    }
    return null
  }

  return (
    <SettingsSection
      description={
        isAdmin ? (
          <Trans>
            Changes apply to sprints created from now on; existing sprints are not modified.
          </Trans>
        ) : (
          <Trans>Only workspace admins can change these settings.</Trans>
        )
      }
      icon={IconCalendarClockOutline18}
      title={t`Cadence`}
    >
      <SettingsRow description={t`The day each new sprint begins on.`} label={t`Start of week`}>
        <Select
          disabled={!isAdmin}
          items={weekdayItems}
          onValueChange={(value) =>
            void updateCadence({ startDayOfWeek: Number(value), workspaceId })
          }
          value={String(cadence.startDayOfWeek)}
        >
          <SelectTrigger aria-label={t`Start of week`} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {weekdayLabels.map((label, dayIndex) => (
              <SelectItem key={label} value={String(dayIndex)}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SettingsRow>

      <SettingsRow
        description={t`How many days each sprint covers.`}
        htmlFor="cadenceLengthDays"
        label={t`Sprint length`}
      >
        <SettingsTextField
          disabled={!isAdmin}
          id="cadenceLengthDays"
          inputMode="numeric"
          max={SPRINT_LENGTH_MAX}
          min={SPRINT_LENGTH_MIN}
          onSave={(value) => updateCadence({ cadenceLengthDays: Number(value), workspaceId })}
          type="number"
          validate={(value) => boundedInteger(value, SPRINT_LENGTH_MIN, SPRINT_LENGTH_MAX)}
          value={String(cadence.cadenceLengthDays)}
        />
      </SettingsRow>

      <SettingsRow
        description={t`How many future sprints to keep pre-generated.`}
        htmlFor="upcomingCount"
        label={t`Upcoming sprints`}
      >
        <SettingsTextField
          disabled={!isAdmin}
          id="upcomingCount"
          inputMode="numeric"
          max={UPCOMING_COUNT_MAX}
          min={0}
          onSave={(value) => updateCadence({ upcomingCount: Number(value), workspaceId })}
          type="number"
          validate={(value) => boundedInteger(value, 0, UPCOMING_COUNT_MAX)}
          value={String(cadence.upcomingCount)}
        />
      </SettingsRow>
    </SettingsSection>
  )
}
